import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { wealthRecentRebalancesLoad } from "../../../../appRedux/ducks/wealthRecentRebalances";

import WSGRStep0 from "./WSGRStep0";
import WSGRStep1 from "./WSGRStep1";
import WSGRStep3 from "./WSGRStep3";
import WSGRModal from "./WSGRModal/WSGRModal";
import WSGREntryModal from "./WSGREntryModal/WSGREntryModal";
import { wealthGuidedRebalancesGet } from "../../../../appRedux/ducks/wealthGuidedRebalancesGet";
import { wealthGuidedRebalancesDelete } from "../../../../appRedux/ducks/wealthGuidedRebalancesDelete";

const WSGuidedRebalancing = ({
  tier,
  wealthPortfolios,
  wealthRecentRebalances,
  wealthGuidedRebalancesGet,
  wealthGuidedRebalancesDelete,
  onExitToPortfolioActivity,
  onStepChange,
  fetchGuidedRebalances,
  loadRecentRebalances,
  deleteGuidedRebalances,
}) => {
  const [step, setStep] = useState(0);
  const [selectedPortfolio, setSelectedPortfolio] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("exit"); // "exit" or "restart"
  const [pendingDeleteUuid, setPendingDeleteUuid] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);

  // Fetch recent rebalances when component mounts or tier changes (only if not already loaded)
  useEffect(() => {
    if (
      tier &&
      !wealthRecentRebalances?.recentRebalances?.[tier] &&
      !wealthRecentRebalances?.loading?.[tier]
    ) {
      loadRecentRebalances(tier);
    }
  }, [tier, loadRecentRebalances, wealthRecentRebalances]);

  // Reset selected portfolios when tier changes
  useEffect(() => {
    setStep(0);
    setSelectedPortfolio("");
    setSelectedDate("");
  }, [tier]);

  // Reset selected portfolios when step resets to 0 (restart guide)
  useEffect(() => {
    if (step === 0) {
      setSelectedPortfolio("");
      setSelectedDate("");
    }
  }, [step]);

  // Check for existing guided rebalances data and navigate to step 3 (now step 2)
  useEffect(() => {
    const guidedRebalancesData =
      wealthGuidedRebalancesGet?.guidedRebalancesData?.[tier];
    const isLoading = wealthGuidedRebalancesGet?.loading?.[tier];

    // Only auto-navigate if we're on step 0 and have data
    // Also check that we're not in the middle of deleting to avoid conflicts
    if (
      step === 0 &&
      !isLoading &&
      !pendingDeleteUuid &&
      guidedRebalancesData &&
      Array.isArray(guidedRebalancesData) &&
      guidedRebalancesData.length > 0
    ) {
      // We have existing guided rebalances, extract portfolio and date info
      const existingData = guidedRebalancesData[0];
      if (existingData.portfolio_id && existingData.rebalance_id) {
        setSelectedPortfolio(existingData.portfolio_id);
        // Extract date from rebalance_id (format: "20250527_portfolio-id")
        const dateFromRebalanceId = existingData.rebalance_id.split("_")[0];
        if (dateFromRebalanceId) {
          const formattedDate = `${dateFromRebalanceId.slice(0, 4)}-${dateFromRebalanceId.slice(4, 6)}-${dateFromRebalanceId.slice(6, 8)}`;
          setSelectedDate(formattedDate);
        }
        setStep(3);
      }
    }
  }, [wealthGuidedRebalancesGet, tier, step, pendingDeleteUuid]);

  // Notify parent of step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step, onStepChange]);

  const handleModalExit = () => {
    setModalOpen(false);
    if (modalAction === "restart") {
      // Restart guide - send DELETE request if we have a UUID, then go to step 0
      if (pendingDeleteUuid) {
        deleteGuidedRebalances(pendingDeleteUuid);
        // Don't change step immediately, wait for DELETE to complete
      } else {
        setStep(0);
      }
    } else {
      // Exit to portfolio activity
      if (onExitToPortfolioActivity) onExitToPortfolioActivity();
    }
  };

  // Watch for DELETE completion and handle cleanup
  useEffect(() => {
    if (pendingDeleteUuid) {
      const isDeleting =
        wealthGuidedRebalancesDelete?.deleting?.[pendingDeleteUuid];
      console.log("DELETE state check:", {
        pendingDeleteUuid,
        modalAction,
        isDeleting,
        deleteState: wealthGuidedRebalancesDelete?.deleting,
        step,
      });

      // If DELETE completed (isDeleting changed from true/undefined to false)
      if (isDeleting === false) {
        console.log("DELETE completed, cleaning up");

        // Clear the guided rebalances data to prevent auto-navigation back to step 3
        fetchGuidedRebalances(tier);

        // For modal-based actions, also handle step changes
        if (modalAction === "restart") {
          console.log("Modal restart DELETE completed, setting step to 0");
          setStep(0);
        }

        // Clear pending state regardless of action type
        setPendingDeleteUuid(null);
      }
    }
  }, [
    wealthGuidedRebalancesDelete,
    pendingDeleteUuid,
    modalAction,
    step,
    fetchGuidedRebalances,
    tier,
  ]);

  // Handle close button for steps 1, restart from step 3 (show modal)
  const handleCloseWithModal = (action = "exit", uuid = null) => {
    setModalAction(action);
    if (action === "restart" && uuid) {
      setPendingDeleteUuid(uuid);
    }
    setModalOpen(true);
  };

  // Handle entry from Step 0 "Begin" button
  const handleBeginClick = () => {
    // Check if entry modal has been shown in this session
    const modalShown = sessionStorage.getItem("guidedRebalancingModalShown");
    if (!modalShown || modalShown !== "true") {
      // Show entry modal first
      setShowEntryModal(true);
    } else {
      // Modal already shown, proceed directly to step 1
      setStep(1);
    }
  };

  // Handle entry modal confirmation
  const handleEntryModalConfirm = () => {
    sessionStorage.setItem("guidedRebalancingModalShown", "true");
    setShowEntryModal(false);
    setStep(1);
  };

  // Handle direct navigation with background delete (no modal)
  const handleDirectNavigation = (targetStep, uuid = null) => {
    // Navigate immediately
    setStep(targetStep);

    // If we have a UUID, set it as pending to prevent auto-navigation conflicts
    if (uuid) {
      setPendingDeleteUuid(uuid);
      deleteGuidedRebalances(uuid);
    }
  };

  return (
    <div className="ws-guided-rebalancing">
      {step === 0 && <WSGRStep0 setStep={handleBeginClick} />}
      {step === 1 && (
        <WSGRStep1
          setStep={setStep}
          tier={tier}
          wealthPortfolios={wealthPortfolios}
          wealthRecentRebalances={wealthRecentRebalances}
          selectedPortfolio={selectedPortfolio}
          setSelectedPortfolio={setSelectedPortfolio}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          onClose={handleCloseWithModal}
        />
      )}
      {step === 3 && (
        <WSGRStep3
          tier={tier}
          setStep={setStep}
          selectedPortfolio={selectedPortfolio}
          selectedDate={selectedDate}
          onExitToPortfolioActivity={onExitToPortfolioActivity}
          onShowModal={handleCloseWithModal}
          onDirectNavigation={handleDirectNavigation}
        />
      )}

      <WSGRModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onExit={handleModalExit}
      />

      <WSGREntryModal
        open={showEntryModal}
        onClose={() => setShowEntryModal(false)}
        onConfirm={handleEntryModalConfirm}
      />
    </div>
  );
};

const stateToProps = (state) => ({
  wealthPortfolios: state.wealthPortfolio,
  wealthRecentRebalances: state.wealthRecentRebalances,
  wealthGuidedRebalancesGet: state.wealthGuidedRebalancesGet,
  wealthGuidedRebalancesDelete: state.wealthGuidedRebalancesDelete,
});

const dispatchToProps = (dispatch) => ({
  fetchGuidedRebalances: (tier) => dispatch(wealthGuidedRebalancesGet(tier)),
  loadRecentRebalances: (tier) => dispatch(wealthRecentRebalancesLoad(tier)),
  deleteGuidedRebalances: (uuid) =>
    dispatch(wealthGuidedRebalancesDelete(uuid)),
});

export default connect(stateToProps, dispatchToProps)(WSGuidedRebalancing);
